/**
 * Platform-internal docs (plans, migration, proposals) — not product Overview/Indeks nav.
 *
 * Index default visibility is `public`. Docs with explicit `visibility: internal`
 * are hidden from product Indeks/search; docs-owned internals also appear under
 * «Plattform / intern». Path set is a fallback until all plans have frontmatter.
 */

export const PLATFORM_INTERNAL_DOC_PATHS = new Set([
  '/docs/core/DOCUMENTATION_ARCHITECTURE_RULE.md',
  '/docs/core/DOCUMENTATION_EXECUTION_PLAN.md',
  '/docs/core/DOCUMENTATION_EXECUTION_PLAN_PHASE2.md',
  '/docs/core/documentation-migration-inventory.md',
  '/docs/core/documentation-migration-plan.md',
  '/docs/core/DOCUMENTATION_CLEANUP_INVENTORY.md',
  '/docs/core/DOCUMENTATION_INVENTORY_PHASE1.md',
  '/docs/core/component-preview-technical-proposal.md',
  '/docs/core/PREVIEW_RUNTIME_PILOT.md',
  '/docs/core/DOCUMENTATION_VALIDATION.md',
  '/docs/ai/AI_GENERATOR_PROPOSAL.md',
  '/docs/core/DOCUMENT_CLASSIFICATION_PROPOSAL.md',
  '/docs/core/DOCUMENT_SIDEBAR_PROPOSAL.md',
  '/docs/core/TAGGING_SYSTEM_PROPOSAL.md',
  '/docs/integrations/MICROSOFT_CEO_AGENT_PLAN.md',
  '/docs/modules/BILLING_PLAN_PROPOSAL.md',
  '/docs/modules/CONTRACT_LINES_MOCKUP.md',
  '/docs/modules/RISK_MODULES_PROPOSAL.md'
]);

const PLATFORM_INTERNAL_TAGS = new Set([
  'execution-plan',
  'migration',
  'ownership',
  'cleanup',
  'inventory'
]);

export function toDocumentPortalPath(documentOrPath) {
  if (!documentOrPath) return null;
  if (typeof documentOrPath === 'string') {
    return documentOrPath.split('#')[0];
  }
  if (documentOrPath.path) {
    return String(documentOrPath.path).split('#')[0];
  }
  if (documentOrPath.sourcePath?.startsWith('public/docs/')) {
    return `/${documentOrPath.sourcePath.replace(/^public\//, '')}`;
  }
  return null;
}

export function isPlatformInternalDocPath(path) {
  return PLATFORM_INTERNAL_DOC_PATHS.has(toDocumentPortalPath(path));
}

function hasPlatformInternalTags(document) {
  return (document?.tags ?? []).some((tag) =>
    PLATFORM_INTERNAL_TAGS.has(String(tag).toLowerCase())
  );
}

function isExplicitInternal(document) {
  return String(document?.visibility ?? '').toLowerCase() === 'internal';
}

/**
 * Documents that belong under «Plattform / intern» (docs-owned plans / ownership).
 */
export function isPlatformNavDocument(document) {
  if (!document || document.source !== 'docs') return false;

  const portalPath = toDocumentPortalPath(document);
  if (isPlatformInternalDocPath(portalPath)) return true;
  if (isExplicitInternal(document)) return true;

  if (document.type === 'migration') return true;
  if (document.type === 'architecture' && hasPlatformInternalTags(document)) return true;
  if (document.type === 'operations' && hasPlatformInternalTags(document)) return true;

  return false;
}

/**
 * True when a document should be hidden from product-facing Overview / Indeks.
 */
export function isPlatformInternalDocument(document) {
  if (!document) return false;
  if (isExplicitInternal(document)) return true;
  if (isPlatformNavDocument(document)) return true;
  if (document.type === 'migration') return true;
  return isPlatformInternalDocPath(toDocumentPortalPath(document));
}

/** Product Indeks / module nav — exclude internal platform docs. */
export function isProductFacingDocument(document) {
  return !isPlatformInternalDocument(document);
}

export function filterProductNavItems(items = []) {
  return items.filter((item) => !isPlatformInternalDocPath(item.path));
}
